import React, { useEffect, useState, useRef } from "react";
import { startAuth0Passwordless, webAuth } from "../../services/auth0Api";
import { emailSchema } from "../../yup";
import styles from "./index.module.css";
import { loginJourneyTimer, tracker } from "../../services/mixpanel";
import {
  useCheckAuth0UserStatusMutation,
  useVerifyAndSetupAuth0UserMutation,
  useSubmitContactClientCareForLoginFormMutation,
} from "../../services/api";
import ContactClientCareCard from "./ContactClientCareCard";
import MasterUserLogin from "../MasterUser/MasterUserLogin/MasterUserLogin";
import AuthSplitLayout from "../../components/AuthSplitLayout";
import LoginLoadingInvite from "./LoginLoadingInvite";
import LoginEmailSentCard from "./LoginEmailSentCard";
import LoginForm from "./LoginForm";

const CONTACT_SCENARIO_CONTENT: Record<ClientCareContactScenarioType, { title: string; description: string }> = {
  access_pending: {
    title: "Access Pending",
    description: "We are unable to process your sign-in at this moment. Please provide your information below so our Client Care team can assist you and grant you access as soon as possible.",
  },
  not_found: {
    title: "Check Your Inbox",
    description: "If the email address entered is associated with an account, you will receive a message with instructions. If you do not receive an email, please provide us with your information. We will contact you shortly.",
  },
}

const LoginChoice = () => {
  const [email, setEmail] = useState("");
  const [checkUserStatus] = useCheckAuth0UserStatusMutation();
  const [verifyAndSetupUser] = useVerifyAndSetupAuth0UserMutation();
  const [showMasterLogin, setShowMasterLogin] = useState(false);
  const [contactScenario, setContactScenario] = useState<ClientCareContactScenarioType | null>(null);
  const [showInvitationCard, setShowInvitationCard] = useState(false);
  const [isContactSubmitted, setIsContactSubmitted] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactError, setContactError] = useState("");
  const [submitContactForm] = useSubmitContactClientCareForLoginFormMutation();
  const [hasClickedContinue, setHasClickedContinue] = useState(false);
  const [emailSentType, setEmailSentType] = useState<
    "invitation" | "password_reset" | "verification" | null
  >(null);
  const [loadingAction, setLoadingAction] = useState<
    "password" | "link" | "otp" | "invite" | null
  >(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("invitation") && params.get("organization")
      ? "invite"
      : null;
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [emailDetails, setEmailDetails] = useState<EmailValidationDetails | null>(null);
  const [selectedRole, setSelectedRole] = useState<"admin" | "client">("client");
  const continueRequestId = useRef(0);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitation = urlParams.get("invitation");
    const organization = urlParams.get("organization");
    if (invitation && organization) {
      setLoadingAction("invite");
      webAuth.authorize({
        invitation,
        organization,
        scope: "openid profile email",
      });
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem("auth0Token");
    localStorage.removeItem("idToken");
    tracker.login.pageView();
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoadingAction(null);
        setError("");
        setMessage("");
      }
    };
    
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleContinue = async (e?: React.MouseEvent | null) => {
    if (e) e.preventDefault();
    const currentRequestId = ++continueRequestId.current;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage("");
      setError("Please enter a valid email address.");
      return;
    }
    try {
      setError("");
      setMessage("");
      setIsValidatingEmail(true);
      const response = await checkUserStatus({ email }).unwrap();
      if (currentRequestId !== continueRequestId.current) return;
      const details = response?.data;
      setEmailDetails(details);

      // Blocked User (Not Master)
      if (details?.isBlocked && !details?.isMasterUser) {
        setContactScenario("access_pending");
        return;
      }

      // New User / Not Found (Not Client, Not Master)
      if (!details?.existsInZoho && !details?.isMasterUser) {
        setContactScenario("not_found");
        return;
      }

      // Master User ONLY
      if (details?.isMasterUser && !details?.existsInZoho) {
        setSelectedRole("admin");
        setShowMasterLogin(true);
        return;
      }

      // Client ONLY (Auto-provisioned by backend)
      if (!details?.isMasterUser && details?.existsInZoho) {
        setSelectedRole("client");
        if (details?.emailSentType) {
          setEmailSentType(details.emailSentType);
          setShowInvitationCard(true);
          return;
        }
      }

      // Dual Role (Master + Client)
      if (details?.isMasterUser && details?.existsInZoho) {
        setSelectedRole("client");
      }
      setHasClickedContinue(true);
    } catch (err: any) {
      if (currentRequestId !== continueRequestId.current) return;
      setEmailDetails(null);
      setHasClickedContinue(false);
      const errMsg =
        err?.data?.message ||
        err?.message ||
        "Failed to validate email.";
      setError(errMsg);
    } finally {
      if (currentRequestId === continueRequestId.current) {
        setIsValidatingEmail(false);
      }
    }
  };

  const validateLoginPrerequisites = () => {
    if (!emailDetails) {
      setMessage("");
      setError("Please enter a valid email address first.");
      return false;
    }
    const isActingAsClient = emailDetails.existsInZoho && selectedRole === "client";
    if (emailDetails.isMasterUser && !isActingAsClient) {
      setShowMasterLogin(true);
      return false;
    }
    if (emailDetails.isBlocked) {
      setContactScenario("access_pending");
      return false;
    }
    if (!emailDetails.existsInZoho) {
      setContactScenario("not_found");
      return false;
    }
    return true;
  };

  const checkProvisioning = async (method: "password" | "magic-link" | "otp", eventType: "emailSubmit" | "success") => {
    if (!emailDetails) return false;
    const isFullyProvisioned = emailDetails.existsInAuth0 && emailDetails.isPasswordSet && emailDetails.isEmailVerified;
    if (!isFullyProvisioned) {
      const response = await verifyAndSetupUser({ email }).unwrap();
      if (response?.data?.emailSentType != null) {
        setEmailSentType(response.data.emailSentType);
        setShowInvitationCard(true);
        setLoadingAction(null);
        tracker.login[eventType](email, method);
        return false;
      } else if (method === "otp" && !response?.data?.existsInAuth0) {
        setLoadingAction(null);
        const failMsg =
          "Access Denied: The email address is not currently registered to receive a code via email.  Please reset your password or contact us for additional help.";
        tracker.login.failed(email, "otp", failMsg);
        setError(failMsg);
        return false;
      }
    }
    return true;
  };

  const handlePasswordLogin = async (e?: React.MouseEvent | null) => {
    if (e) e.preventDefault();
    loginJourneyTimer.start();
    if (!validateLoginPrerequisites()) return;
    try {
      setError("");
      setLoadingAction("password");
      if (!(await checkProvisioning("password", "emailSubmit"))) return;
      
      tracker.login.emailSubmit(email, "password");
      webAuth.authorize({ login_hint: email, prompt: "login"});
    } catch (err: any) {
      setLoadingAction(null);
      const errMsg =
        err?.data?.message ||
        err.message ||
        "Failed to initiate password login.";
      setError(errMsg);
      tracker.login.failed(email, "password", errMsg);
    }
  };

  const handleSendMagicLink = async (e?: React.MouseEvent | null) => {
    if (e) e.preventDefault();
    loginJourneyTimer.start();
    if (!validateLoginPrerequisites()) return;
    try {
      setLoadingAction("link");
      setError("");
      setMessage("");
      if (!(await checkProvisioning("magic-link", "success"))) return;
      
      await startAuth0Passwordless(email, "link");
      tracker.login.success(email, "magic-link");
      setMessage("Magic link sent! Please check your inbox.");
    } catch (err: any) {
      setMessage("");
      const auth0Reason = err.message;
      let finalError = "";
      if (auth0Reason === "no_account") {
        finalError =
          "Access Denied: You must have an existing account to use Magic Links. Please sign up with a password first.";
      } else {
        finalError = auth0Reason || "Failed to send magic link.";
      }
      setError(finalError);
      tracker.login.failed(email, "magic-link", finalError);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSendOtpCode = async (e?: React.MouseEvent | null) => {
    if (e) e.preventDefault();
    loginJourneyTimer.start();
    if (!validateLoginPrerequisites()) return;
    try {
      setLoadingAction("otp");
      setError("");
      setMessage("");
      if (!(await checkProvisioning("otp", "success"))) return;
      
      tracker.login.success(email, "otp");
      webAuth.authorize({
        connection: "email",
        login_hint: email,
        prompt: "login"
      });
    } catch (err: any) {
      setLoadingAction(null);
      setMessage("");
      const errMsg = err.message || "Failed to start login process.";
      setError(errMsg);
      tracker.login.failed(email, "otp", errMsg);
    }
  };

  const handleContactSubmit = async (data: { firstName: string; lastName: string; email: string }) => {
    setContactError("");
    setIsSubmittingContact(true);
    try {
      await submitContactForm(data);
      setIsContactSubmitted(true);
    } catch (err: any) {
      setContactError(err?.data?.message || "Failed to submit information. Please try again.");
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const isAnyLoading = loadingAction !== null;

  return (
    <AuthSplitLayout>
      {loadingAction === "invite" ? (
        <LoginLoadingInvite />
      ) : showMasterLogin ? (
        <MasterUserLogin
          email={email}
          onBack={() => {
            setShowMasterLogin(false);
            setError("");
            setMessage("");
          }}
        />
      ) : contactScenario ? (
        <ContactClientCareCard
          title={CONTACT_SCENARIO_CONTENT[contactScenario].title}
          description={CONTACT_SCENARIO_CONTENT[contactScenario].description}
          initialEmail={email}
          isContactSubmitted={isContactSubmitted}
          isSubmittingContact={isSubmittingContact}
          contactError={contactError}
          onSubmitForm={handleContactSubmit}
          onClearError={() => setContactError("")}
          onCancel={() => {
            setSelectedRole("client");
            setError("");
            setMessage("");
            setContactScenario(null);
            setIsContactSubmitted(false);
            setContactError("");
          }}
        />
      ) : showInvitationCard ? (
        <LoginEmailSentCard
          emailSentType={emailSentType}
          onBackToLogin={() => {
            setError("");
            setMessage("");
            setEmailSentType(null);
            setShowInvitationCard(false);
          }}
        />
      ) : (
        <LoginForm
          email={email}
          setEmail={(val) => {
            continueRequestId.current++;
            setIsValidatingEmail(false);
            setEmail(val);
            setError("");
            setMessage("");
            setHasClickedContinue(false);
            setEmailDetails(null);
          }}
          error={error}
          setError={setError}
          message={message}
          setMessage={setMessage}
          isAnyLoading={isAnyLoading}
          loadingAction={loadingAction}
          isValidatingEmail={isValidatingEmail}
          emailDetails={emailDetails}
          hasClickedContinue={hasClickedContinue}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          handleContinue={handleContinue}
          handlePasswordLogin={handlePasswordLogin}
          handleSendMagicLink={handleSendMagicLink}
          handleSendOtpCode={handleSendOtpCode}
        />
      )}
    </AuthSplitLayout>
  );
};

export default LoginChoice;